import debug from 'debug';
import EventTarget$1 from '@ungap/event-target';

/**
 * Converts an array of Promises to an array of objects that represent the
 * settled state of each Promise. A Promise is considered settled if it has
 * either fulfilled or rejected.
 *
 * @param promises - An array of Promises to be settled.
 * @returns A Promise that resolves to an array of objects containing a
 * `status` property that is either "fulfilled" or "rejected", and a
 * corresponding property (`value` for fulfilled Promises, `reason` for rejected
 * Promises).
 */
const allSettled = (promises) => {
  const wrappedPromises = promises.map((p) =>
    Promise.resolve(p).then(
      (val) => ({ status: 'fulfilled', value: val }),
      (err) => ({ status: 'rejected', reason: err })
    )
  );

  return Promise.all(wrappedPromises);
};

function _optionalChain$2(ops) { let lastAccessLHS = undefined; let value = ops[0]; let i = 1; while (i < ops.length) { const op = ops[i]; const fn = ops[i + 1]; i += 2; if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) { return undefined; } if (op === 'access' || op === 'optionalAccess') { lastAccessLHS = value; value = fn(value); } else if (op === 'call' || op === 'optionalCall') { value = fn((...args) => value.call(lastAccessLHS, ...args)); lastAccessLHS = undefined; } } return value; }/**
 * Represents the state of an OpenPromise instance.
 */
var OpenPromiseState; (function (OpenPromiseState) {
  const Idle = 'idle'; OpenPromiseState["Idle"] = Idle;
  const Pending = 'pending'; OpenPromiseState["Pending"] = Pending;
  const Fulfilled = 'fulfilled'; OpenPromiseState["Fulfilled"] = Fulfilled;
  const Rejected = 'rejected'; OpenPromiseState["Rejected"] = Rejected;
})(OpenPromiseState || (OpenPromiseState = {}));

/**
 * An implementation of a Promise that allows the executor function to be
 * executed lazily and provides access to the resolve and reject functions.
 *
 * @typeparam T The type of the resolved value of the promise.
 */
class PromiseResolvedError extends Error {
  __init() {this.name = 'PromiseResolvedError';}

  constructor() {
    super('Promise already resolved / rejected');PromiseResolvedError.prototype.__init.call(this);  }
}

class PromiseExecutedError extends Error {
  __init2() {this.name = 'PromiseExecutedError';}

  constructor() {
    super('Promise already executed, cannot be executed again');PromiseExecutedError.prototype.__init2.call(this);  }
}

/**
 * A type that defines the `resolve` function for a Promise instance.
 */










class OpenPromise {
   __init3() {this._resolve = null;}

   __init4() {this._reject = null;}

  __init5() {this.resolvedValue = null;}

  /**
   * The rejected reason of the promise. Set to null until the promise has been
   * rejected.
   */
  __init6() {this.rejectedReason = null;}

  /**
   * The current state of the promise.
   */
  __init7() {this.state = OpenPromiseState.Idle;}

  /**
   * The underlying promise instance.
   */
  

  /**
   * Attaches callbacks for the resolution and/or rejection of the Promise.
   *
   * @param onFulfilled The callback to execute when the Promise is resolved.
   * @param onRejected The callback to execute when the Promise is rejected.
   * @returns A Promise for the completion of which ever callback is executed.
   */
  

  /**
   * Attaches a callback for only the rejection of the Promise.
   *
   * @param onRejected The callback to execute when the Promise is rejected.
   * @returns A Promise for the completion of the callback.
   */
  

  /**
   * Attaches a callback for the resolution and/or rejection of the Promise.
   * The callback is executed when the Promise is settled, whether fulfilled or
   * rejected.
   *
   * @param onFinally The callback to execute when the Promise is settled.
   * @returns A Promise for the completion of the callback.
   */
  

  /**
   * A string representing the object type. In this case, it is set to
   * `'OpenPromise'`.
   */
   __init8() {this[Symbol.toStringTag] = 'OpenPromise';}

  /**
   * Creates a new OpenPromise instance.
   *
   * @param executor An optional function to be executed immediately upon
   *   construction.
   * @param lazy Whether or not to delay execution until the `execute()` method
   *   is called.
   */
  constructor(
     executor = null,
     lazy = false
  ) {this.executor = executor;this.lazy = lazy;OpenPromise.prototype.__init3.call(this);OpenPromise.prototype.__init4.call(this);OpenPromise.prototype.__init5.call(this);OpenPromise.prototype.__init6.call(this);OpenPromise.prototype.__init7.call(this);OpenPromise.prototype.__init8.call(this);OpenPromise.prototype.__init9.call(this);OpenPromise.prototype.__init10.call(this);OpenPromise.prototype.__init11.call(this);
    this.promise = new Promise((resolve, reject) => {
      this._resolve = resolve;
      this._reject = reject;

      if (!lazy && executor) {
        this.execute();
      }
    });

    this.then = this.promise.then.bind(this.promise);
    this.catch = this.promise.catch.bind(this.promise);
    this.finally = this.promise.finally.bind(this.promise);
  }

  /**
   * Executes the Promise if it has not already been executed.
   */
  __init9() {this.execute = () => {
    if (this.state !== OpenPromiseState.Idle) {
      throw new PromiseExecutedError();
    }

    if (!this.executor) {
      throw new TypeError('Executor must be defined to execute this task');
    }

    this.state = OpenPromiseState.Pending;
    _optionalChain$2([this, 'access', _3 => _3.executor, 'optionalCall', _4 => _4(this.resolve, this.reject)]);
  };}

  /**
   * Resolves the Promise with the given value.
   *
   * @param value The value with which to resolve the Promise.
   */
  __init10() {this.resolve = (value) => {
    if (
      this.state !== OpenPromiseState.Pending &&
      this.state !== OpenPromiseState.Idle
    ) {
      throw new PromiseResolvedError();
    }

    this.state = OpenPromiseState.Fulfilled;

    Promise.resolve(value).then((resolvedValue) => {
      this.resolvedValue = resolvedValue;
    });

    this._resolve(value);
  };}

  /**
   * A function that is used to reject the promise with a reason.
   *
   * @param reason The reason why the promise was rejected.
   */
  __init11() {this.reject = (reason) => {
    if (
      this.state !== OpenPromiseState.Pending &&
      this.state !== OpenPromiseState.Idle
    ) {
      throw new PromiseResolvedError();
    }

    this.state = OpenPromiseState.Rejected;

    this.rejectedReason = reason;

    this._reject(reason);
  };}
}

function _optionalChain$1(ops) { let lastAccessLHS = undefined; let value = ops[0]; let i = 1; while (i < ops.length) { const op = ops[i]; const fn = ops[i + 1]; i += 2; if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) { return undefined; } if (op === 'access' || op === 'optionalAccess') { lastAccessLHS = value; value = fn(value); } else if (op === 'call' || op === 'optionalCall') { value = fn((...args) => value.call(lastAccessLHS, ...args)); lastAccessLHS = undefined; } } return value; }/**
 * Represents an event with additional data of type `T`.
 *
 * @typeparam T - The type of the additional data attached to the event.
 */
class CustomEvent extends Event {
  /**
   * Additional data attached to the event.
   */
  __init() {this.detail = undefined;}

  /**
   * Creates a new instance of `CustomEvent`.
   *
   * @param type - The type of the event.
   * @param options - An object containing options to customize the event.
   *   The `detail` property of the `options` object can be used to attach
   *   additional data of type `T` to the event.
   */
  constructor(type, options) {
    super(type, options);CustomEvent.prototype.__init.call(this);    if (_optionalChain$1([options, 'optionalAccess', _ => _.detail])) {
      this.detail = options.detail;
    }
  }
}

/**
 * Determines if the `performance.now()` function is supported by the current
 * environment.
 */
const supportsPerformanceNow =
  typeof performance !== 'undefined' && typeof performance.now === 'function';

/**
 * Determines if the `IdleDeadline` interface is supported by the current
 * environment.
 */
const supportsTimeRemaining =
  typeof IdleDeadline !== 'undefined' &&
  typeof (IdleDeadline ).timeRemaining === 'function';

/**
 * Returns the current time in milliseconds, using `performance.now()` if
 * available, otherwise using `Date.now()`.
 * @returns The current time in milliseconds.
 */
const now = () => {
  if (supportsPerformanceNow) {
    return performance.now();
  }
  return Date.now();
};

/**
 * The target frame period, in milliseconds.
 */
const STABLE_FRAME_PERIOD = 17;
/**
 * The default time to use as the deadline when calculating
 * `IdleDeadline.timeRemaining()`, in milliseconds.
 */
const IDLE_TIME_REMAINING = 14;

let lastFrameTime = now();
let initialized = false;
/**
 * An array used to store the frame periods over time for calculating the
 * average frame period.
 */
const framePeriodBuffer = Array(20).fill(Infinity);
/**
 * The current position in the `framePeriodBuffer`.
 */
let framePeriodBufferPosition = 0;
/**
 * The current average frame period, in milliseconds.
 */
let framePeriod = 0;

/**
 * Returns the current average frame period, in milliseconds.
 * @returns The current average frame period, in milliseconds.
 */
const getFramePeriod = () => framePeriod;

/**
 * Initializes the `framePeriodBuffer` and starts an animation frame loop to
 * continuously update the buffer.
 */
const initializeTimeRemaining = () => {
  if (initialized) {
    return;
  }

  initialized = true;

  /**
   * Updates the `framePeriodBuffer` and `framePeriod`.
   */
  const updateFrameTime = () => {
    framePeriodBuffer[framePeriodBufferPosition] = now() - lastFrameTime;
    framePeriodBufferPosition =
      (framePeriodBufferPosition + 1) % framePeriodBuffer.length;
    let ΣPeriod = 0;

    for (let i = 0; i < framePeriodBuffer.length; i += 1) {
      ΣPeriod += framePeriodBuffer[i];
    }

    framePeriod = ΣPeriod / framePeriodBuffer.length;
    lastFrameTime = now();

    globalThis.requestAnimationFrame(updateFrameTime);
  };

  if (typeof window !== 'undefined' && !supportsTimeRemaining) {
    globalThis.requestAnimationFrame(updateFrameTime);
  }
};

/**
 * Calculates the time remaining until the deadline, either using the
 * `IdleDeadline.timeRemaining()` method if available,
 * or by estimating the time remaining based on the current time and the
 * previous frame time.
 * @param deadline The deadline to calculate the time remaining for, in
 *   milliseconds. Defaults to `IDLE_TIME_REMAINING`.
 * @returns The time remaining until the deadline, in milliseconds.
 */
const mockTimeRemaining = (deadline = IDLE_TIME_REMAINING) => {
  const timeRemaining = Math.max(deadline - (now() - lastFrameTime), 0);
  return timeRemaining;
};

/**
 * Calculates the time remaining until the deadline, either using the
 * `IdleDeadline.timeRemaining()` method if available,
 * or by estimating the time remaining based on the current time and the
 * previous frame time.
 * @param deadline The deadline to calculate the time remaining for, in
 * milliseconds. Defaults to `IDLE_TIME_REMAINING`.
 * @returns The time remaining until the deadline, in milliseconds.
 */
const timeRemaining = (deadline = IDLE_TIME_REMAINING) => {
  if (!initialized) {
    initializeTimeRemaining();
  }

  if (supportsTimeRemaining) {
    return (IdleDeadline ).timeRemaining();
  }
  return mockTimeRemaining(deadline);
};

class CustomEventPolyfill extends Event {
  

  constructor(message, data) {
    super(message, data);
    this.detail = data.detail;
  }
}

/**
 * Interface representing the details of a queue update event.
 */















/**
 * A custom event for updates to a queue.
 */
class QueueUpdateEvent extends CustomEventPolyfill {
  /**
   * Creates a new `QueueUpdateEvent` instance.
   * @param queue An object representing the state of the queue.
   * @param action The update action that was taken on the queue.
   */
  constructor(
    queue,
    action
  ) {
    super('update', {
      detail: {
        remainTasks: queue.remainTasks,
        clearedTasks: queue.clearedTasks,
        action,
      },
    });
  }
}

/**
 * An enum representing different update actions that can be taken on a queue.
 */
var QueueUpdateAction; (function (QueueUpdateAction) {
  /**
   * New task was added to the queue.
   */
  const Add = 'add'; QueueUpdateAction["Add"] = Add;
  /**
   * Some task was removed from the queue.
   */
  const Remove = 'remove'; QueueUpdateAction["Remove"] = Remove;
  /**
   * Some tasks was executed and removed from the queue.
   */
  const Clear = 'clear'; QueueUpdateAction["Clear"] = Clear;
  /**
   * All tasks was executed.
   */
  const Drain = 'drain'; QueueUpdateAction["Drain"] = Drain;
})(QueueUpdateAction || (QueueUpdateAction = {}));

/**
 * Custom error class for when an OpenPromise is not lazy.
 */
class NotLazyOpenPromiseError extends Error {
  /**
   * The name of the error class.
   */
  __init() {this.name = 'NotLazyOpenPromiseError';}

  /**
   * Creates a new instance of the error with a custom message.
   */
  constructor() {
    super('OpenPromise must be lazy');NotLazyOpenPromiseError.prototype.__init.call(this);  }
}

const log$2 = debug('promise:seq-q');

/**
 * SequentialQueue is a queue of tasks that executes tasks in sequence up to a
 * certain concurrency limit.
 * Tasks can be either a synchronous function or an OpenPromise (a lazy promise
 * that will only execute when executed).
 *
 * @typeparam T The type of the result produced by the OpenPromise task.
 */
class SequentialQueue extends EventTarget$1 {
  /**
   * The queue of tasks to execute.
   */
   __init() {this.queue = [];}
  /**
   * The number of tasks that have been cleared from the queue.
   */
   __init2() {this.internalClearedTasks = 0;}

  /**
   * A map of QueuedTask objects to their associated task IDs.
   */
   __init3() {this.taskMap = new Map();}

  /**
   * Creates a new SequentialQueue object.
   *
   * @param concurrency The number of tasks to execute concurrently.
   *   Defaults to 1.
   * @param dependencyQueue A queue of tasks that must complete before tasks in
   *   this queue can be executed. Defaults to undefined.
   * @param queueId An optional ID for this queue. If not specified, a random ID
   *   will be generated.
   */
  constructor(
     concurrency = 1,
     dependencyQueue,
      queueId = Math.random().toString(36).substring(2)
  ) {
    super();this.concurrency = concurrency;this.dependencyQueue = dependencyQueue;this.queueId = queueId;SequentialQueue.prototype.__init.call(this);SequentialQueue.prototype.__init2.call(this);SequentialQueue.prototype.__init3.call(this);  }

  /**
   * Returns the number of tasks remaining in the queue.
   */
  get remainTasks() {
    return this.queue.length;
  }

  /**
   * Returns the number of tasks in the queue.
   */
  get length() {
    return this.queue.length;
  }

  /**
   * Returns the number of tasks that have been cleared from the queue.
   */
  get clearedTasks() {
    return this.internalClearedTasks;
  }

  /**
   * Adds a task to the queue.
   *
   * @param task The task to add to the queue.
   * @param taskId An optional ID for the task. If specified, the task will be
   * added to the taskMap with this ID.
   */
  add(task, taskId) {
    this.dispatchEvent(new QueueUpdateEvent(this, QueueUpdateAction.Add));
    this.queue.push(task);

    if (taskId) {
      this.taskMap.set(task, taskId);
    }
  }

  /**
   * Removes a task from the queue.
   *
   * @param task The task to remove from the queue.
   */
  remove(task) {
    const index = this.queue.indexOf(task);
    if (index > -1) {
      this.queue.splice(index, 1);
    }

    if (this.taskMap.has(task)) {
      this.taskMap.delete(task);
    }

    this.dispatchEvent(new QueueUpdateEvent(this, QueueUpdateAction.Remove));
  }

  /**
   * Executes the next set of tasks in the queue, up to the concurrency limit.
   * If a dependencyQueue is provided and has remaining tasks, the method
   * returns immediately without executing any tasks.
   * Returns a Promise that resolves with an array of results from any
   * OpenPromise tasks that were executed during this tick.
   */
  tick() {
    const openPromiseTasks = [];

    if (this.dependencyQueue && this.dependencyQueue.remainTasks !== 0) {
      log$2(
        `[${this.queueId}] Waiting for the dependency queue with ${this.dependencyQueue.remainTasks} tasks.`
      );
      return Promise.resolve([]);
    }

    let taskCleared = 0;

    for (let i = 0; i < this.concurrency; i += 1) {
      taskCleared += 1;
      const task = this.queue.shift();

      if (task) {
        if (this.taskMap.has(task)) {
          this.taskMap.delete(task);
        }

        if ('execute' in task) {
          if (!task.lazy) {
            throw new NotLazyOpenPromiseError();
          }
          task.execute();
          openPromiseTasks.push(task);
        } else {
          task();
        }
      }
    }

    if (taskCleared > 0) {
      log$2(`[${this.queueId}] ${taskCleared} task cleared.`);
    }

    this.internalClearedTasks += taskCleared;
    this.dispatchEvent(new QueueUpdateEvent(this, QueueUpdateAction.Clear));

    return allSettled(openPromiseTasks);
  }
}

const log$1 = debug('promise:par-q');

/**
 * A queue that processes tasks asynchronously in frames.
 */
class FrameFillingQueue extends EventTarget$1 {
  /**
   * The tasks currently in the queue.
   */
   __init() {this.queue = [];}

  /**
   * A map of tasks to their unique identifiers.
   */
   __init2() {this.taskMap = new Map();}

  /**
   * The number of cleared tasks.
   */
  __init3() {this.clearedTasks = 0;}

  /**
   * The number of rejected promises.
   */
  __init4() {this.rejected = 0;}

  /**
   * The number of resolved promises.
   */
  __init5() {this.resolved = 0;}

  /**
   * The number of tasks currently being processed.
   */
  __init6() {this.working = 0;}

  /**
   * Returns the number of remaining tasks in the queue.
   */
  get remainTasks() {
    return this.queue.length;
  }

  /**
   * Creates a new instance of `FrameFillingQueue`.
   *
   * @param concurrent - The maximum number of tasks to execute concurrently.
   * @param dependencyQueue - An optional dependency queue that must be empty
   *   before tasks in this queue can be executed.
   * @param queueId - An optional unique identifier for the queue.
   * @param protectedTime - The amount of time (in milliseconds) that must
   *   remain in the current frame before the next task can be executed.
   */
  constructor(
     concurrent = 1,
     dependencyQueue,
      queueId = Math.random().toString(36).substring(2),
     protectedTime = 2
  ) {
    super();this.concurrent = concurrent;this.dependencyQueue = dependencyQueue;this.queueId = queueId;this.protectedTime = protectedTime;FrameFillingQueue.prototype.__init.call(this);FrameFillingQueue.prototype.__init2.call(this);FrameFillingQueue.prototype.__init3.call(this);FrameFillingQueue.prototype.__init4.call(this);FrameFillingQueue.prototype.__init5.call(this);FrameFillingQueue.prototype.__init6.call(this);FrameFillingQueue.prototype.__init7.call(this);FrameFillingQueue.prototype.__init8.call(this);FrameFillingQueue.prototype.__init9.call(this);FrameFillingQueue.prototype.__init10.call(this);  }

  /**
   * The number of tasks in the queue.
   */
  get length() {
    return this.queue.length;
  }

  /**
   * Adds a task to the queue.
   *
   * @param task - The task to add to the queue.
   * @param taskId - An optional unique identifier for the task.
   */
  __init7() {this.add = (task, taskId) => {
    this.dispatchEvent(new QueueUpdateEvent(this, QueueUpdateAction.Add));
    this.queue.push(task);

    if (taskId) {
      this.taskMap.set(task, taskId);
    }

    this.tick();
  };}

  /**
   * Removes a task from the queue.
   *
   * @param task - The task to remove from the queue.
   * @param silent - An optional flag to indicate whether to suppress the
   *   `QueueUpdateEvent`.
   */
  remove(task, silent) {
    const index = this.queue.indexOf(task);
    if (index > -1) {
      this.queue.splice(index, 1);
    }

    if (this.taskMap.has(task)) {
      this.taskMap.delete(task);
    }

    if (!silent) {
      this.dispatchEvent(new QueueUpdateEvent(this, QueueUpdateAction.Remove));
    }
  }

  /**
   * Executes a task and returns a promise that resolves when the task is
   * complete.
   *
   * @param task - The task to execute.
   */
   __init8() {this.executeTask = (task) => {
    this.remove(task, true);

    if ('execute' in task) {
      if (!task.lazy) {
        throw new NotLazyOpenPromiseError();
      }
      task.execute();
      return task;
    }

    return Promise.resolve(task());
  };}

  /**
   * Handles a promise in the queue, updating the relevant counters and
   * triggering the execution of the next task if the concurrency limit allows
   * it.
   *
   * @param p - The promise to handle.
   * @param results - An array to which the promise will be pushed.
   * @returns A promise that resolves when the input promise has settled.
   */
   __init9() {this.handlePromise = async (
    p,
    results
  ) => {
    this.working += 1;

    try {
      try {
        await p;
        this.resolved += 1;
      } catch (e2) {
        this.rejected += 1;
      }
    } finally {
      this.clearedTasks += 1;
      this.working -= 1;

      const deltaT = timeRemaining();

      if (
        this.queue.length &&
        this.working < this.concurrent &&
        deltaT > this.protectedTime
      ) {
        const promise = this.executeTask(this.queue[0]);
        results.push(promise);
        this.handlePromise(promise, results);
      }

      if (!this.queue.length) {
        this.dispatchEvent(new QueueUpdateEvent(this, QueueUpdateAction.Clear));
      }
    }
  };}

  /**
   * Executes the next available task in the queue. If the queue is not empty
   * and the number of working tasks is less than `concurrent`, a task will be
   * removed from the queue and executed.
   *
   * @returns A promise that resolves when all tasks have completed.
   */
  __init10() {this.tick = () => {
    if (this.dependencyQueue && this.dependencyQueue.remainTasks !== 0) {
      log$1(
        `[${this.queueId}] Waiting for the dependency queue with ${this.dependencyQueue.remainTasks} tasks.`
      );
      return Promise.resolve([]);
    }

    if (this.working > 0) return Promise.resolve([]);

    const results = [];
    for (let i = 0; i < Math.min(this.queue.length, this.concurrent); i += 1) {
      const promise = this.executeTask(this.queue[i]);
      results.push(promise);
      this.handlePromise(promise, results);
    }

    return allSettled(results);
  };}
}

const log = debug('promise:time-slice');
const logGroup = debug('promise:time-slices');

logGroup.log = console.groupCollapsed.bind(console);

const MAX_TASK_DELAY_TIME = 2000;

/**
 * The `QueueType` enum represents the two types of queues that can be used in
 * the `TimeSlicingQueue` class:
 * a sequential queue or a frame-filling queue.
 */
var QueueType; (function (QueueType) {
  const Sequential = 'sequential'; QueueType["Sequential"] = Sequential;
  const FrameFilling = 'frame-filling'; QueueType["FrameFilling"] = FrameFilling;
})(QueueType || (QueueType = {}));

/**
 * The `TimeSlicingQueue` class is a task queue that schedules tasks to run
 * at a later time to avoid blocking the main thread.
 *
 * @template T The type of the queued tasks.
 */
class TimeSlicingQueue extends EventTarget {
  /**
   * A flag indicating whether the queue is currently running.
   */
  __init() {this.running = false;}

  /**
   * Indicates whether a tick has been scheduled.
   *
   * @private
   */
   __init2() {this.tickScheduled = false;}

  /**
   * The time of the last successful tick.
   *
   * @private
   */
   __init3() {this.lastSuccessfulTickTime = 0;}

  /**
   * The queue used to store tasks.
   *
   * @private
   */
  

  /**
   * Returns a map of the tasks in the queue and their corresponding IDs.
   */
  get taskMap() {
    return this.queue.taskMap;
  }

  /**
   * Returns the number of tasks in the queue.
   */
  get length() {
    return this.queue.length;
  }

  /**
   * Returns the number of tasks in the queue.
   *
   * This property is identical to `length` and is provided for
   * compatibility with the `Queue` interface.
   */
  get remainTasks() {
    return this.queue.length;
  }

  /**
   * Constructs a new `TimeSlicingQueue` object.
   *
   * @param concurrency The maximum number of tasks that can be run
   *   concurrently.
   * @param type The type of the queue to use: either a sequential queue or a
   *   frame-filling queue.
   * @param protectedTime The minimum amount of time (in milliseconds) between
   *   ticks to ensure that the main thread is not blocked.
   * @param dependencyQueue A dependency queue to use.
   * @param queueId An optional ID to use for the queue.
   */
  constructor(
     concurrency = 1,
     type = QueueType.FrameFilling,
     protectedTime = 2,
     dependencyQueue,
      queueId = Math.random().toString(36).substring(2)
  ) {
    super();this.concurrency = concurrency;this.type = type;this.protectedTime = protectedTime;this.dependencyQueue = dependencyQueue;this.queueId = queueId;TimeSlicingQueue.prototype.__init.call(this);TimeSlicingQueue.prototype.__init2.call(this);TimeSlicingQueue.prototype.__init3.call(this);TimeSlicingQueue.prototype.__init4.call(this);TimeSlicingQueue.prototype.__init5.call(this);TimeSlicingQueue.prototype.__init6.call(this);TimeSlicingQueue.prototype.__init7.call(this);TimeSlicingQueue.prototype.__init8.call(this);TimeSlicingQueue.prototype.__init9.call(this);
    if (type === QueueType.FrameFilling) {
      this.queue = new SequentialQueue(concurrency, dependencyQueue, queueId);
    } else {
      this.queue = new FrameFillingQueue(
        concurrency,
        dependencyQueue,
        queueId,
        protectedTime
      );

      this.queue.addEventListener(QueueUpdateAction.Add, (event) =>
        this.dispatchEvent(event)
      );
      this.queue.addEventListener(QueueUpdateAction.Remove, (event) =>
        this.dispatchEvent(event)
      );
      this.queue.addEventListener(QueueUpdateAction.Clear, (event) =>
        this.dispatchEvent(event)
      );
    }

    initializeTimeRemaining();
    log(`[${this.queueId}] initialized`);
  }

  /**
   * Runs the queue.
   */
  __init4() {this.run = () => {
    if (this.running) {
      return;
    }

    this.running = true;

    this.scheduleTick();
  };}

  /**
   * Adds a task to the queue.
   * @param task The task to add.
   * @param taskId An optional ID to assign to the task.
   */
  __init5() {this.add = (task, taskId) => {
    this.queue.add(task, taskId);

    if (this.running) {
      this.scheduleTick();
    }
  };}

  get remove() {
    return this.queue.remove;
  }

  /**
   * Logs the tasks that are still in the queue.
   *
   * @private
   * @param title The title of the log.
   */
   __init6() {this.logRemainedTask = (title = `Remained Tasks`) => {
    logGroup(title);
    log(
      ['', ...[...this.queue.taskMap.values()].map((x) => `* ${x}`)].join(
        '\r\n'
      )
    );
    console.groupEnd();
  };}

  /**
   * Schedules a tick to process the next task in the queue.
   *
   * @private
   */
   __init7() {this.scheduleTick = () => {
    if (this.tickScheduled) {
      return;
    }

    this.tickScheduled = true;

    globalThis.requestAnimationFrame(() => {
      // log(`[${this.queueId}] New frame running`);
      this.tickScheduled = false;
      this.tickOnce();
    });
  };}

  /**
   * Processes the next task in the queue.
   *
   * @private
   */
   __init8() {this.tickOnce = () => {
    if (!this.running) {
      log(`[${this.queueId}] queue is not running, won't tick`);
      return;
    }

    if (!this.queue.length) {
      log(`[${this.queueId}] Task queue drained`);
      return;
    }

    const deltaT = timeRemaining();
    const currentTime = now();

    if (currentTime - this.lastSuccessfulTickTime > MAX_TASK_DELAY_TIME) {
      this.logRemainedTask(
        `[${this.queueId}] [Force] Δt=${deltaT}, ${this.queue.length} in queue`
      );
      this.lastSuccessfulTickTime = currentTime;
      this.queue.tick().then(this.scheduleTick);
    } else if (deltaT > this.protectedTime) {
      this.logRemainedTask(
        `[${this.queueId}] Δt=${deltaT}, ${this.queue.length} in queue`
      );
      this.lastSuccessfulTickTime = currentTime;
      this.queue.tick().then(this.scheduleTick);
    } else if (this.queue.length > 0) {
      this.logRemainedTask(
        `[${this.queueId}] Δt=${deltaT}, protected time reached, scheduling next tick, ${this.queue.length} tasks delayed`
      );
      this.scheduleTick();
    }
  };}

  /**
   * Stops the queue.
   */
  __init9() {this.stop = () => {
    this.running = false;
  };}
}

function _nullishCoalesce(lhs, rhsFn) { if (lhs != null) { return lhs; } else { return rhsFn(); } } function _optionalChain(ops) { let lastAccessLHS = undefined; let value = ops[0]; let i = 1; while (i < ops.length) { const op = ops[i]; const fn = ops[i + 1]; i += 2; if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) { return undefined; } if (op === 'access' || op === 'optionalAccess') { lastAccessLHS = value; value = fn(value); } else if (op === 'call' || op === 'optionalCall') { value = fn((...args) => value.call(lastAccessLHS, ...args)); lastAccessLHS = undefined; } } return value; }
/**
 * A number representing the ID of the last canceled handle.
 */
let lastCancelHandleId = 0;
/**
 * A set of numbers representing the IDs of tasks in the task queue.
 */
const taskQueue = new Set();

/**
 * A mock implementation of the `requestIdleCallback` function that uses the
 * browser's `requestAnimationFrame` to schedule tasks when the browser is
 * idle. This is used as a fallback when `requestIdleCallback` is not
 * supported by the browser.
 */
const mockRequestIdleCallback = (
  callback,
  options
) => {
  // A number representing the ID of the current handle.
  const handleId = lastCancelHandleId + 1;
  // Increment the lastCancelHandleId with one for the next task.
  lastCancelHandleId = handleId;

  // Add the handleId to the task queue.
  taskQueue.add(handleId);

  // The time at which the task is scheduled.
  const scheduleTime = now();
  // An `IdleDeadline` object that is passed to the task's callback function
  // when it is executed.
  const idleDeadline = {
    timeRemaining,
    didTimeout: true,
  };

  // A function that attempts to run the task when the browser is idle.
  const tryTask = () => {
    // If the task has been canceled, do nothing.
    if (!taskQueue.has(handleId)) return;

    // The time at which the task is executed.
    const runningTime = now();
    // A boolean indicating whether the task has timed out.
    const didTimeout =
      runningTime - scheduleTime > (_nullishCoalesce(_optionalChain([options, 'optionalAccess', _ => _.timeout]), () => ( Infinity)));

    /**
     * If the browser is currently idle or the task has timed out, execute the
     * task and remove it from the task queue. Otherwise, wait until the next
     * animation frame to try again.
     */
    if (getFramePeriod() <= STABLE_FRAME_PERIOD || didTimeout) {
      idleDeadline.didTimeout = didTimeout;
      callback(idleDeadline);
      taskQueue.delete(handleId);
    } else {
      globalThis.requestAnimationFrame(tryTask);
    }
  };

  // Schedule the task to run on the next animation frame.
  globalThis.requestAnimationFrame(tryTask);

  // Return the handleId for the task.
  return handleId;
};

/**
 * A mock implementation of the `cancelIdleCallback` function that removes
 * the task with the specified handle ID from the task queue. This is used as
 * a fallback when `cancelIdleCallback` is not supported by the browser.
 */
const mockCancelIdleCallback = (
  handleId
) => {
  taskQueue.delete(handleId);
};

/**
 * The `requestIdleCallback` function to use, either the native implementation
 * or the fallback implementation if the native implementation is not
 * supported by the browser.
 */
const requestIdleCallback =
  typeof _optionalChain([globalThis, 'optionalAccess', _2 => _2.requestIdleCallback]) === 'undefined'
    ? mockRequestIdleCallback
    : globalThis.requestIdleCallback;

/**
 * Cancels a scheduled idle callback previously registered with
 * `requestIdleCallback()`.
 */
const cancelIdleCallback =
  typeof _optionalChain([globalThis, 'optionalAccess', _3 => _3.cancelIdleCallback]) === 'undefined'
    ? mockCancelIdleCallback
    : globalThis.cancelIdleCallback;

export { CustomEvent, FrameFillingQueue, IDLE_TIME_REMAINING, NotLazyOpenPromiseError, OpenPromise, OpenPromiseState, PromiseExecutedError, PromiseResolvedError, QueueType, QueueUpdateAction, QueueUpdateEvent, STABLE_FRAME_PERIOD, SequentialQueue, TimeSlicingQueue, allSettled, cancelIdleCallback, getFramePeriod, initializeTimeRemaining, now, requestIdleCallback, timeRemaining };
//# sourceMappingURL=module.js.map
